import { FC, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Dropdown, Image, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';
import { useTranslation } from 'react-i18next';
import bell from 'assets/images/bell_white.svg';
import empty from 'assets/images/empty_notifications.svg';
import { useAppSelector } from 'store/hooks';
import { selectUserIsLoggedIn } from 'store/user/selectors';
import {
  NotificationRow,
  fetchNotifications,
  fetchUnreadCount
} from 'services/api/notifications';

import {
  SectionTitle,
  StyledContainer,
  StyledContent,
  StyledEmpty,
  StyledHeader,
  StyledIcon,
  StyledList,
  TabItem
} from './styled';
import NotificationItem from '../NotificationItem';

const { Title, Text } = Typography;

const PAGE_LIMIT = 20;

const tabItems = [
  {
    id: 'all',
    title: 'all'
  },
  {
    id: 'unread',
    title: 'unread'
  }
];

const Notifications: FC = () => {
  // translations
  const { t } = useTranslation();
  // context
  const isLoggedIn = useAppSelector(selectUserIsLoggedIn);
  // states
  const [selected, setSelected] = useState('all');
  const [page, setPage] = useState(1);
  const [results, setResults] = useState<NotificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [open, setOpen] = useState(false);
  // refs
  const contentRef = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (pageToLoad: number, tab: string, append: boolean) => {
      if (!isLoggedIn) {
        return;
      }

      setLoading(true);

      try {
        const res = await fetchNotifications({
          page: pageToLoad,
          limit: PAGE_LIMIT,
          read: tab === 'all' ? undefined : false
        });

        setTotal(res.total);
        setResults(prev => (append ? [...prev, ...res.data] : res.data));
      } catch {
        // keep whatever is already rendered
      } finally {
        setLoading(false);
      }
    },
    [isLoggedIn]
  );

  const refreshUnread = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    try {
      setHasUnread(!!(await fetchUnreadCount()));
    } catch {
      // keep the previous dot state
    }
  }, [isLoggedIn]);

  useEffect(() => {
    setPage(1);
    load(1, selected, false);
  }, [load, selected]);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    if (page > 1) {
      load(page, selected, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const refresh = useCallback(() => {
    setPage(1);
    load(1, selected, false);
    refreshUnread();
  }, [load, selected, refreshUnread]);

  useEffect(() => {
    const handleScroll = () => {
      const isAtBottom =
        contentRef.current &&
        contentRef.current.scrollTop + contentRef.current.clientHeight >=
          contentRef.current.scrollHeight;

      if (isAtBottom && !loading && total > results.length) {
        setPage(prev => prev + 1);
      }
    };

    contentRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      contentRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [loading, results?.length, total]);

  const renderDate = useCallback(
    (date: string) => {
      const today = new Date();
      const yesterday = dayjs().subtract(1, 'day');

      if (date === dayjs(today).format(DATE_FORMAT)) {
        return t('notifications.today');
      }

      if (date === dayjs(yesterday).format(DATE_FORMAT)) {
        return t('notifications.earlier');
      }

      return date;
    },
    [t]
  );

  const sortedData = useMemo(() => {
    const groupedData = results.reduce(
      (acc: { [key: string]: NotificationRow[] }, item) => {
        const createdAt = new Date(item.createdAt || '');
        const dateKey = dayjs(createdAt).format(
          DATE_FORMAT
        ) as keyof typeof acc;

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey]?.push(item);

        return acc;
      },
      {}
    );

    const resultArray = Object.entries(groupedData).map(([date, data]) => {
      return {
        title: renderDate(date),
        data: data as ReadonlyArray<NotificationRow>
      };
    });

    return resultArray;
  }, [renderDate, results]);

  const onOpenChange = (open: boolean) => {
    setOpen(open);

    if (open) {
      refresh();
    }
  };

  const handleMenuClick = () => {
    setOpen(false);
  };

  return (
    <Dropdown
      onOpenChange={onOpenChange}
      open={open}
      dropdownRender={() => (
        <StyledContainer>
          <StyledContent>
            <StyledHeader>
              <Title level={4} style={{ margin: 0 }}>
                {t('notifications.title')}
              </Title>
              <Space direction="horizontal" size={6}>
                {tabItems.map(item => (
                  <TabItem
                    selected={selected === item.id}
                    key={item.id}
                    onClick={() => setSelected(item.id)}
                  >
                    <Text>{t(`notifications.${item.title}`)}</Text>
                  </TabItem>
                ))}
              </Space>
            </StyledHeader>
            {sortedData.length ? (
              <StyledList ref={contentRef}>
                {sortedData.map((item, index) => (
                  <Space
                    key={`${item.title}-${index}`}
                    direction="vertical"
                    style={{ width: '100%' }}
                  >
                    <div>
                      <SectionTitle>{item.title}</SectionTitle>
                      {item.data.map((notification, i) => (
                        <NotificationItem
                          key={`${i}-${notification.id}`}
                          onClick={handleMenuClick}
                          item={notification}
                          refresh={refresh}
                        />
                      ))}
                    </div>
                  </Space>
                ))}
              </StyledList>
            ) : (
              <StyledEmpty
                image={
                  <Image src={empty} preview={false} width={160} height={120} />
                }
                description={t(
                  `notifications.${
                    selected === 'all'
                      ? 'no_notifications'
                      : 'no_unread_notifications'
                  }`
                )}
              />
            )}
          </StyledContent>
        </StyledContainer>
      )}
      placement="bottomRight"
      trigger={['click']}
    >
      <StyledIcon $showDot={hasUnread}>
        <Image src={bell} width={18} alt="employ" preview={false} />
      </StyledIcon>
    </Dropdown>
  );
};

export default Notifications;
